import React, { useState, useRef, useEffect } from 'react';
import { X, Plus } from '@phosphor-icons/react';
import './TagDropdown.scss';

interface TagDropdownProps {
  selectedTags: string[];
  availableTags: string[];
  onChange: (tags: string[]) => void;
  onClose: () => void;
}

const TagDropdown: React.FC<TagDropdownProps> = ({
  selectedTags,
  availableTags,
  onChange,
  onClose
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Small delay to prevent immediate closing when the button is clicked
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const filteredTags = availableTags
    .filter(tag => 
      !selectedTags.includes(tag) && 
      tag.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice(0, 10);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !selectedTags.includes(trimmedTag)) {
      onChange([...selectedTags, trimmedTag]);
    }
    setSearchQuery('');
    setFocusedIndex(-1);
  };

  const removeTag = (tagToRemove: string) => {
    onChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && filteredTags[focusedIndex]) {
        addTag(filteredTags[focusedIndex]);
      } else if (searchQuery.trim()) {
        addTag(searchQuery);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => 
        prev < filteredTags.length ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => prev > -1 ? prev - 1 : -1);
    } else if (e.key === 'Backspace' && searchQuery === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  return (
    <div className="tasksFieldDropdown" ref={dropdownRef}>
      <div className="tasksFieldDropdownPortal">
        <div className="tasksFieldDropdownSearch">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setFocusedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Add tags..."
            className="tasksFieldDropdownSearchInput"
          />
        </div>

        {/* Selected tags */}
        {selectedTags.length > 0 && (
          <div className="tasksTagDropdownSelected">
            {selectedTags.map(tag => (
              <span key={tag} className="tasksTagDropdownTag">
                <span className="tasksTagDropdownTagText">#{tag}</span>
                <button
                  type="button"
                  className="tasksTagDropdownTagRemove"
                  onClick={() => removeTag(tag)}
                  title="Remove tag"
                >
                  <X size={10} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Available tags or create new */}
        <div className="tasksFieldDropdownOptions">
          {filteredTags.map((tag, index) => (
            <button
              key={tag}
              type="button"
              className={`tasksFieldDropdownOption ${index === focusedIndex ? 'tasksFieldDropdownOptionFocused' : ''}`}
              onClick={() => addTag(tag)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <span className="tasksFieldDropdownOptionIcon">#</span>
              <span className="tasksFieldDropdownOptionLabel">{tag}</span>
            </button>
          ))}
          
          {searchQuery.trim() && !filteredTags.includes(searchQuery.trim()) && (
            <button
              type="button"
              className={`tasksFieldDropdownOption tasksFieldDropdownOptionCreate ${
                focusedIndex === filteredTags.length ? 'tasksFieldDropdownOptionFocused' : ''
              }`}
              onClick={() => addTag(searchQuery)}
              onMouseEnter={() => setFocusedIndex(filteredTags.length)}
            >
              <Plus size={14} weight="bold" className="tasksFieldDropdownOptionIcon" />
              <span className="tasksFieldDropdownOptionLabel">Create "{searchQuery.trim()}"</span>
            </button>
          )}

          {filteredTags.length === 0 && !searchQuery.trim() && selectedTags.length === 0 && (
            <div className="tasksFieldDropdownEmpty">No tags yet. Type to create one.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagDropdown;


